import { isComplianceSubdomain, isPurposeOfShipmentRequired } from './getSubdomain';
import { isHsnCodeRequired } from './exportShipment';
import { BoxType, ShipmentDataType } from '../types/shipment';
import { formatPendingRequirementLabel } from './fieldLabels';
import { validateCpscCompliance, parseBoolean, normalizeCpscDisclaimCode } from './usCompliance';
import { hasAdminAccess } from '../services/hasAdminAccess';
import { isEUCountry, isPlacedShipment } from './euCompliance';

export interface PendingRequirementItem {
  label: string;
  boxIndex?: number;
}

export interface GetPendingRequirementsParams {
  boxes: BoxType[];
  shipmentType: ShipmentDataType;
  extraDetails?: any;
  consignee?: any;
  isShipmentExport: boolean;
  complianceRequired: boolean;
  euComplianceRule: { isRequired: boolean; [key: string]: any };
  isUSConsignee: boolean;
  isHSNValidating?: boolean;
  isProductFormInvalid?: boolean;
  customHostname?: string;
  isForwarded?: boolean;
  shipmentById?: any;
}

/**
 * Detects whether a shipment has been forwarded, has an AWB generated, or has reached in-transit.
 */
export function isShipmentForwarded(shipment?: any): boolean {
  if (!shipment) return false;
  const status = (shipment.status || '').toLowerCase();
  if (status === 'forwarded') return true;
  if (shipment.carrierDetails?.awb?.trim?.()) return true;
  if (typeof shipment.awb === 'string' && shipment.awb.trim()) return true;
  if (typeof shipment.carrierAWB === 'string' && shipment.carrierAWB.trim()) return true;
  if (shipment.isFinalForward === true) return true;
  if (shipment.hasReachedInTransit === true) return true;
  if (Array.isArray(shipment.statusDetails) && shipment.statusDetails.some((d: any) => d.status === 'in_transit')) {
    return true;
  }
  return false;
}

export const getPendingRequirements = (params: GetPendingRequirementsParams): PendingRequirementItem[] => {
  const isForwarded = params.isForwarded ?? isShipmentForwarded(params.shipmentById);
  if (isForwarded) {
    return [];
  }

  const {
    boxes,
    shipmentType,
    extraDetails,
    consignee,
    isShipmentExport,
    complianceRequired,
    euComplianceRule,
    isUSConsignee,
    isHSNValidating,
    isProductFormInvalid,
  } = params;

  const list: PendingRequirementItem[] = [];
  const isComplianceActive = isComplianceSubdomain(params.customHostname || extraDetails?.customHostname);
  const destCountry = (consignee?.country as any)?.name || consignee?.country || '';
  const isDestEU = isComplianceActive && isEUCountry(destCountry);
  const isDocument = (shipmentType?.type || '').toLowerCase() === 'document';

  if (!boxes || boxes.length === 0) {
    list.push({ label: 'Boxes (At least 1 box is required)' });
    return list;
  }

  if (isHSNValidating) {
    list.push({ label: 'HSN code is currently verifying' });
  }

  if (isProductFormInvalid) {
    list.push({ label: 'Complete active product entry or cancel edit' });
  }

  boxes.forEach((box, idx) => {
    const boxNum = idx + 1;
    const weight = Number((box.weight && `${box.weight}`) || 0);

    if (!weight || weight <= 0) {
      list.push({ label: formatPendingRequirementLabel('weight', { boxNumber: boxNum }), boxIndex: idx });
    }

    if (!box.products || box.products.length === 0) {
      list.push({
        label: `Box ${boxNum} — Declared Goods (At least 1 declared item is required)`,
        boxIndex: idx,
      });
    } else {
      box.products.forEach((p: any, pIdx: number) => {
        const rowNum = pIdx + 1;
        if (!p.description?.trim()) {
          list.push({
            label: formatPendingRequirementLabel('description', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        if (!p.quantity || Number(p.quantity) <= 0) {
          list.push({
            label: formatPendingRequirementLabel('quantity', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        if (p.unitValue === undefined || p.unitValue === null || Number(p.unitValue) <= 0) {
          list.push({
            label: formatPendingRequirementLabel('unitValue', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        const hsnRequired = isHsnCodeRequired({
          isShipmentExport,
          customHostname: params.customHostname || extraDetails?.customHostname,
          shipmentType,
        });
        if (hsnRequired && !p.HSNCode?.trim()) {
          list.push({
            label: formatPendingRequirementLabel('HSNCode', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }

        // EU Compliance: M-PID and NS-PID are pending only when euComplianceRule.isRequired is true
        // (EU destination + B2C/ECOM + Sale + <= €150)
        if (!isDocument && complianceRequired && euComplianceRule?.isRequired) {
          if (!p.merchantProductId?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('merchantProductId', {
                boxNumber: boxNum,
                productRowNumber: rowNum,
              }),
              boxIndex: idx,
            });
          }
          if (!p.nonStandardManufacturerProductId?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('nonStandardManufacturerProductId', {
                boxNumber: boxNum,
                productRowNumber: rowNum,
              }),
              boxIndex: idx,
            });
          }
        }

        if (isComplianceActive && !isDocument && complianceRequired && isUSConsignee) {
          const rawCpsc = p.cpscCompliance || (p as any).compliance?.us || (p as any).cpsc;
          const disclaimObj = p.disclaimMessageSet || rawCpsc?.disclaimMessageSet || {};
          const refObj = p.referenceMessageSet || rawCpsc?.referenceMessageSet || {};
          const resolvedCpsc: any = {
            beingSold: parseBoolean(rawCpsc?.beingSold ?? p.cpscBeingSold ?? p.isRegulated),
            disclaimCode: normalizeCpscDisclaimCode(
              rawCpsc?.disclaimCode ?? p.cpscDisclaimCode ?? disclaimObj?.disclaimCode ?? p.disclaimCode,
            ),
            intendedUseCode: (
              rawCpsc?.intendedUseCode ??
              p.cpscIntendedUseCode ??
              disclaimObj?.intendedUseCode ??
              p.intendedUseCode ??
              ''
            ).trim(),
            additionalContext: (
              rawCpsc?.additionalContext ??
              p.cpscAdditionalContext ??
              disclaimObj?.intendedUseDescription ??
              disclaimObj?.additionalContext ??
              p.additionalContext ??
              ''
            ).trim(),
            certificateFiled: parseBoolean(rawCpsc?.certificateFiled ?? p.cpscCertificateFiled ?? p.isRegistered),
            productIdRegistry: (
              rawCpsc?.productIdRegistry ??
              p.cpscProductIdRegistry ??
              refObj?.registryProductId ??
              p.productIdRegistry ??
              ''
            ).trim(),
            certificateVersionId: (
              rawCpsc?.certificateVersionId ??
              p.cpscCertificateVersionId ??
              refObj?.productVersion ??
              p.certificateVersionId ??
              ''
            ).trim(),
            certifierId: (
              rawCpsc?.certifierId ??
              p.cpscCertifierId ??
              refObj?.certifierId ??
              p.certifierId ??
              ''
            ).trim(),
            productId: (rawCpsc?.productId ?? p.cpscProductId ?? p.productId ?? '').trim(),
            productIdType: (rawCpsc?.productIdType ?? p.cpscProductIdType ?? p.productIdType ?? 'SKU').trim(),
          };

          const cpscVal = validateCpscCompliance(resolvedCpsc, true, true, hasAdminAccess() || isPlacedShipment());
          if (!cpscVal.isValid) {
            Object.keys(cpscVal.errors).forEach(errKey => {
              list.push({
                label: formatPendingRequirementLabel(errKey, {
                  boxNumber: boxNum,
                  productRowNumber: rowNum,
                }),
                boxIndex: idx,
              });
            });
          }
        }
      });
    }
  });

  // Shipment Mode-driven requirements
  if (!shipmentType?.shipmentMode?.trim()) {
    list.push({ label: formatPendingRequirementLabel('shipmentMode') });
  }

  // Recipient VAT / Tax ID — only required after shipment is placed, and only for B2B + EU destination
  const isB2BMode = (shipmentType?.shipmentMode || '').toUpperCase() === 'B2B';
  const isVatRequired = !isDocument && isPlacedShipment() && isB2BMode && isDestEU;
  const vatIdVal = (consignee?.vat || shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || '').trim();
  if (isVatRequired && !vatIdVal) {
    list.push({ label: formatPendingRequirementLabel('recipientVatTaxId') });
  }

  // Purpose of Shipment — required unless Document or exempt subdomain (wyngs, wal)
  const isPurposeRequired = isPurposeOfShipmentRequired(
    shipmentType,
    params.customHostname || extraDetails?.customHostname,
  );
  const purposeVal = (extraDetails?.purposeOfShipment || '').trim();
  if (isPurposeRequired && !purposeVal) {
    list.push({ label: formatPendingRequirementLabel('purposeOfShipment') });
  }

  // Incoterm and DDP/DDU requirements — only when visible / post-placed for export parcel shipments
  if (isPlacedShipment() && isShipmentExport && !isDocument) {
    const incotermVal = (shipmentType?.incoterm || extraDetails?.incoterm || '').trim().toUpperCase();
    if (!incotermVal) {
      list.push({ label: formatPendingRequirementLabel('incoterm') });
    } else if (incotermVal === 'CIF') {
      const frVal =
        shipmentType?.freight !== undefined && shipmentType?.freight !== ''
          ? shipmentType.freight
          : extraDetails?.freight;
      const insVal =
        shipmentType?.freightInsurance !== undefined && shipmentType?.freightInsurance !== ''
          ? shipmentType.freightInsurance
          : extraDetails?.freightInsurance;
      if (!frVal || Number(frVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freight') });
      }
      if (!insVal || Number(insVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freightInsurance') });
      }
    } else if (incotermVal === 'CNF') {
      const frVal =
        shipmentType?.freight !== undefined && shipmentType?.freight !== ''
          ? shipmentType.freight
          : extraDetails?.freight;
      if (!frVal || Number(frVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freight') });
      }
    }

    const rawDuty =
      shipmentType?.dutyCharge ??
      extraDetails?.dutyCharge ??
      params.shipmentById?.dutyCharge ??
      params.shipmentById?._packageId?.dutyCharge;
    const dutyType =
      typeof rawDuty === 'string'
        ? rawDuty.trim().toUpperCase()
        : typeof rawDuty === 'object' && typeof rawDuty?.type === 'string'
        ? rawDuty.type.trim().toUpperCase()
        : '';
    if (!dutyType || (dutyType !== 'DDP' && dutyType !== 'DDU')) {
      list.push({ label: formatPendingRequirementLabel('dutyCharge') });
    }
  }

  return list;
};
